import React, { useCallback, useEffect, useState } from 'react';

import { DateRangeSelectorDropdown } from '../../..';

/**
 * Configuration options that create a DateRangeFilter component based on a
 * DateRangeSelectorDropdown.
 * @typedef {MakeDateRangeFilterInput}
 * @property {DateRangeOption} defaultOption a default date range which will be initially selected
 * @property {string} elementId the HTML id to apply to the input element
 * @property {number} height optional height for DropdownInput
 * @property {number} width optional width for DropdownInput
 */
interface MakeDateRangeFilterInput {
    getDefaultOption(): DateRangeOption;
    height?: number;
    useModal(): ModalProviderValue;
    width?: number;
}

/**
 * Returns a React component which renders a DateRangeSelectorDropdown that allows the user to
 * select a date range. See `./filterByDateRange.ts` for details on how the selected option is used
 * to filter the rows in the table.
 * @param input {MakeDateRangeFilterInput<D>}
 */

export function makeDateRangeFilter<D extends PublisherAndWriterItem>({
    getDefaultOption,
    height = 45,
    useModal,
    width = 200,
}: MakeDateRangeFilterInput): ColumnFilterComponent<D> {
    const defaultOption = getDefaultOption();

    const DateRangeFilter = ({ column }: ColumnFilterProps<D>): JSX.Element => {
        const modal = useModal();
        const { setFilter, filterValue } = column;
        const [dateRange, setDateRange] = useState<MaybeEmptyDateRange>(
            defaultOption.value,
        );
        const [selectedLabel, setSelectedLabel] = useState(defaultOption.label);

        const setOption = useCallback(
            (nextOption: DropdownOption<MaybeEmptyDateRange>): void => {
                setFilter(nextOption.value);
                setDateRange(nextOption.value);
                setSelectedLabel(nextOption.label);
            },
            [setFilter],
        );

        useEffect(() => {
            if (filterValue === undefined) {
                setOption(defaultOption);
            }
        }, [filterValue, defaultOption]);

        return (
            <DateRangeSelectorDropdown
                borderConfig="solid.small"
                dateRange={dateRange}
                initialOption={defaultOption}
                selectedLabel={selectedLabel}
                setSelectedLabel={setSelectedLabel}
                setDateRange={(nextValue: MaybeEmptyDateRange): void => {
                    setDateRange(nextValue);
                    setFilter(nextValue);
                }}
                height={height}
                modal={modal}
                width={width}
            />
        );
    };

    return DateRangeFilter;
}
